import { useBoolean, useNumber, useString, useWindowSize } from "helpers/hooks"
import SimpleTable, { SimpleTableProps } from "components/Table/SimpleTable"
import { useCallback, useEffect, useState } from "react"
import { Avatar } from "components/Avatar"
import { Pagination } from "components/Pagination/Pagination"
import { TippyCustomzie, useTippyLayout } from "components/TippyCustomzie"
import ActionItem from "pages/projects/molecules/ActionItem"
import { ReactComponent as IconActionThreeDot } from "assets/images/icons/icon-three-dots.svg"
import { cloneDeep, debounce } from "lodash"
import { ClearAll, FilterIcon } from "components/Filter"
import FormSelectFilter from "pages/conversations/molecules/FormSelectFilter"
import { FilterHeader } from "components/Filter/FilterHeader"
import { FilterCollapse } from "components/Filter/FilterCollapse"
import Button from "components/Button/Button"
import { useAppSelector } from "hooks/useApp"
import { pushTo } from "helpers/history"
import { PERMISSIONS } from "constants/permissions"
import { PATH } from "constants/path"
import ButtonHasIcon from "components/Button/ButtonHasIcon"
import { AddMemberToGroup } from "./Modal/AddMemberToGroup"
import { ChangeGroupRoleModal } from "./Modal/ChangeGroupRoleModal"
import { configureStore } from "stores/configureStore"
import { closeModal, openModal } from "reducers/modal"
import {
  getAllGroupMemberMiddleware,
  getGroupsOrganizationRolesMiddleware,
  postUnassignMemberFromGroupsOrganizationMiddleware,
} from "./groups.api"
import { toast } from "react-toastify"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import {
  GroupMemberQueryState,
  GroupMemberResponse,
  GroupsOrganizationRoleResponse,
} from "./groups.type"
import { SearchInput } from "components/Search/SearchInput"
import { useGroupPermission } from "./GroupPermissionContext"

interface Props {
  groupId: string
  callGroupsOrganizationDetail: () => void
}

export const GroupMemberPage = (props: Props) => {
  const { groupId, callGroupsOrganizationDetail } = props
  const { isMobile } = useWindowSize()
  const userInfo = useAppSelector((state) => state.userInfo)
  const { permissions: groupPermissions } = useGroupPermission()

  const [users, setUsers] = useState<GroupMemberResponse[]>([])
  const [queryState, setQueryState] = useState<GroupMemberQueryState>({
    page: 1,
    pageSize: 25,
    search: "",
    roles: "",
    sort: "",
    order: "",
  })
  const [roles, setRoles] = useState<GroupsOrganizationRoleResponse[]>([])
  const totalPage = useNumber(0)
  const search = useString()
  const [showTippy, setShowTippy] = useState([] as string[])
  const showFilter = useBoolean(false)
  const [filterRole, setFilterRole] = useState<{ [key: string]: string }>({
    "-1": "-1",
  })
  const { TippyLayout } = useTippyLayout()

  const openModalAddMember = useBoolean(false)
  const [changeRoleModal, setChangeRoleModal] = useState({
    open: false,
    memberId: "",
    currentRoleId: "",
  })

  const canAddMember = groupPermissions.includes(
    PERMISSIONS.GROUP_ORGANIZATION_ADD_MEMBER
  )
  const canRemoveMember = groupPermissions.includes(
    PERMISSIONS.GROUP_ORGANIZATION_REMOVE_MEMBER
  )
  const canChangeRole = groupPermissions.includes(
    PERMISSIONS.GROUP_ORGANIZATION_ADD_MEMBER
  )

  useEffect(() => {
    if (!userInfo?.id) {
      return
    }
    if (!userInfo.last_organization_id) {
      pushTo(PATH.notFound)
      return
    }
  }, [userInfo?.id])

  useEffect(() => {
    getAllUser(queryState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryState.pageSize,
    queryState.page,
    queryState.search,
    queryState.roles,
    queryState.sort,
    queryState.order,
  ])

  useEffect(() => {
    getAllRole()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getAllUser = async (newQueyState: GroupMemberQueryState) => {
    const response = await getAllGroupMemberMiddleware(groupId, {
      ...newQueyState,
    })
    setUsers(response.data)
    totalPage.setValue(response.total)
  }

  const getAllRole = async () => {
    const response = await getGroupsOrganizationRolesMiddleware()
    setRoles(response.data)
  }

  const handleChangePage = (newPage: number, newLimit: number) => {
    setQueryState({
      ...queryState,
      page: newPage,
      pageSize: newLimit,
    })
  }
  const handleClickTippy = (value) => {
    if (showTippy.includes(value)) {
      setShowTippy(showTippy.filter((item) => item !== value))
    } else setShowTippy(showTippy.concat([value]))
  }

  const debounceSearch = useCallback(
    debounce((newSearch: string, newQueyState: GroupMemberQueryState) => {
      setQueryState({
        ...newQueyState,
        search: newSearch.trim(),
        page: 1,
      })
    }, 500),
    []
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      search.setValue(value)
      debounceSearch(value, cloneDeep(queryState))
    },
    [debounceSearch, queryState, search]
  )

  const handleSearchClear = useCallback(() => {
    search.setValue("")
    setQueryState({
      ...queryState,
      search: "",
    })
  }, [queryState, search])

  const onCheckActiveFilter = () => {
    return Object.keys(filterRole).length > 0 && !filterRole["-1"]
  }

  const onChangeRoleFilter = (chosenValue: any) => {
    const newChosenValue = cloneDeep(chosenValue)
    setFilterRole((prev) => {
      if (
        Object.keys(newChosenValue).length === 0 ||
        (prev["-1"] !== "-1" && newChosenValue["-1"] === "-1")
      ) {
        return { "-1": "-1" }
      }
      if (
        newChosenValue["-1"] === "-1" &&
        Object.keys(newChosenValue).length > 1
      ) {
        delete newChosenValue["-1"]
      }

      return newChosenValue
    })
  }

  const onClearRoleFilter = () => {
    const roleFilter =
      queryState.roles && queryState.roles !== ""
        ? JSON.parse(queryState.roles).reduce(
            (acc, role) => ({ ...acc, [role]: role }),
            {}
          )
        : { "-1": "-1" }

    setFilterRole(roleFilter)
    showFilter.setValue(false)
  }

  const onApplyFilter = () => {
    showFilter.setValue(false)
    setQueryState({
      ...queryState,
      page: 1,
      roles: (() => {
        const filteredRoles = Object.keys(filterRole)
          .filter((key) => key !== "-1")
          .map((key) => filterRole[key])
        return filteredRoles.length === 0 ? "" : JSON.stringify(filteredRoles)
      })(),
    })
  }

  const handleSort = (
    dataIndex: string,
    sortDirection: "asc" | "desc" | null
  ) => {
    setQueryState({
      ...queryState,
      sort: sortDirection ? dataIndex : "",
      order: sortDirection || "",
      page: 1, // Reset to first page when sorting
    })
  }

  const handleWhenAddMemberSuccess = () => {
    if (queryState.page === 1) {
      getAllUser(queryState)
    } else {
      setQueryState({
        ...queryState,
        page: 1,
      })
    }
    openModalAddMember.setValue(false)
    callGroupsOrganizationDetail()
  }
  const openChangeRoleModal = (columnData: GroupMemberResponse) => {
    setChangeRoleModal({
      open: true,
      memberId: columnData.id,
      currentRoleId: columnData.roleId,
    })
  }
  const handleCloseChangeRoleModal = () => {
    setChangeRoleModal({
      open: false,
      memberId: "",
      currentRoleId: "",
    })
  }
  const handleChangeRoleSuccess = (
    memberId: string,
    newRole: GroupsOrganizationRoleResponse
  ) => {
    setUsers(
      users.map((user) =>
        user.id === memberId
          ? { ...user, roleName: newRole.name, roleId: newRole.id }
          : user
      )
    )
    handleCloseChangeRoleModal()
  }
  const handleRemoveMember = (userId: string) => {
    configureStore.dispatch(
      openModal({
        type: "Delete",
        props: {
          deleteModal: {
            title: (
              <p>
                Removing this member from the group will revoke their access to
                all projects associated with it.
              </p>
            ),
            label: `Warning`,
            content: `This action cannot be undone.`,
            onSubmit: async () => {
              try {
                await postUnassignMemberFromGroupsOrganizationMiddleware({
                  user_id: userId,
                  group_organization_id: groupId,
                })
                callGroupsOrganizationDetail()
                if (queryState.page === 1) {
                  getAllUser(queryState)
                } else {
                  setQueryState({
                    ...queryState,
                    page: 1,
                  })
                }
                closeModal()
                toast(
                  <LabelNotificationPage
                    messenger="Remove member from group successfully!"
                    type="success"
                  />
                )
              } catch (error: any) {
                closeModal()
                toast(
                  <LabelNotificationPage
                    messenger={
                      error?.response?.data?.message ||
                      "Remove member from group failed!"
                    }
                    type="error"
                  />
                )
              }
            },
            styleTitle: { textAlign: "center" },
            titleButton: "Remove member",
            colorYellowButton: true,
          },
        },
      })
    )
  }
  const renderHeader = () => {
    return (
      <div className="flex items-center justify-between h-full px-[24px]">
        <div className="flex items-center"></div>
        <div className="flex items-center gap-[12px]">
          {!isMobile ? (
            <SearchInput
              value={search.value}
              onChange={handleSearchChange}
              onClear={handleSearchClear}
            />
          ) : null}
          {renderFilter()}
          {canAddMember && !isMobile ? (
            <ButtonHasIcon
              title="Add member"
              widthButton={130}
              onClick={() => openModalAddMember.setValue(true)}
            />
          ) : null}
        </div>
      </div>
    )
  }

  const renderFilter = () => {
    const rolerFilters = [
      {
        key: "All",
        value: "-1",
      },
      ...roles.map((role) => ({
        key: role.name,
        value: role.id,
      })),
    ]

    if (isMobile) {
      return (
        <TippyLayout
          visible={showFilter.value}
          headerContent={
            <FilterHeader
              setVisible={showFilter.setValue}
              onCancel={onClearRoleFilter}
              onApply={onApplyFilter}
            />
          }
          mainContent={
            <div>
              <ClearAll
                onClick={() => {
                  setFilterRole({ "-1": "-1" })
                }}
                active={onCheckActiveFilter()}
                containerClass="border-b border-[#E4E4E4]"
              />

              <div className="flex flex-col">
                <FilterCollapse
                  label="Role"
                  multiActive
                  data={rolerFilters.map((el) => ({
                    value: el.value,
                    label: el.key,
                  }))}
                  chosenValue={filterRole}
                  onChange={onChangeRoleFilter}
                />
              </div>
            </div>
          }
        >
          <FilterIcon
            onClick={() => {
              showFilter.setValue(!showFilter.value)
            }}
            activeIcon={queryState.roles ? true : false}
          />
        </TippyLayout>
      )
    }
    return (
      <TippyCustomzie
        containerClass="component-card-menu"
        placement="bottom-end"
        interactive
        arrow={false}
        animation="scale"
        offset={[280, 20]}
        allowHTML
        visible={showFilter.value}
        content={
          <div
            className="pt-3.5 pb-3"
            style={{
              background: "#222222",
              borderRadius: 4,
              width: 250,
            }}
          >
            <div className="flex items-center justify-between mb-3 px-3 select-none">
              <p className="hidden md:block font-semibold text-16 leading-26 text-white">
                Filter
              </p>
              <ClearAll
                onClick={() => {
                  setFilterRole({ "-1": "-1" })
                }}
                active={onCheckActiveFilter()}
              />
            </div>

            <div className="flex">
              <FormSelectFilter
                title="Role"
                type="multi"
                dataSelects={rolerFilters}
                styleKey={{
                  maxWidth: "100%",
                }}
                styleRoot={{
                  width: "100%",
                }}
                objActiveSelect={filterRole}
                setObjActiveSelect={onChangeRoleFilter}
              />
            </div>

            <div className="flex justify-end mt-3 mr-3">
              <Button
                title="Apply"
                onClick={onApplyFilter}
                widthBtn={120}
                heightBtn={42}
              />
            </div>
          </div>
        }
        onClickOutside={onClearRoleFilter}
      >
        <FilterIcon
          onClick={() => {
            showFilter.setValue(!showFilter.value)
          }}
          activeIcon={queryState.roles ? true : false}
        />
      </TippyCustomzie>
    )
  }

  const renderContent = () => {
    return (
      <>
        <div className="flex flex-col p-6">
          <div className="text-black bg-white border border-border-gray rounded-md overflow-auto">
            <SimpleTable
              customClass="storage-usage-table"
              columns={isMobile ? columnsMobile : columns}
              isRequiredTableHeader={isMobile ? false : true}
              dataSource={users}
              onSort={handleSort}
            />
          </div>
          {totalPage.value ? (
            <Pagination
              total={totalPage.value}
              queryState={{
                page: queryState.page,
                limit: queryState.pageSize,
              }}
              handleChangePage={handleChangePage}
            />
          ) : null}
        </div>
      </>
    )
  }
  const columnsMobile: SimpleTableProps["columns"] = [
    {
      title: "Name",
      dataIndex: "fullname",
      render: (columnData: GroupMemberResponse) => {
        return (
          <div className="flex flex-col gap-[8px]">
            <div className="flex items-center gap-[8px]">
              <Avatar
                name={columnData.fullname ?? ""}
                avatar={columnData.avatar ?? ""}
                width="30px"
                height="30px"
              />
              <div className="flex flex-col">
                <p className="font-semibold text-[13px] leading-[22px] text-[#111111]">
                  {columnData.fullname}
                </p>
                <p className="font-normal text-[13px] leading-[22px] text-[#7A7A7A]">
                  {columnData.email}
                </p>
              </div>
            </div>
            <p className="font-normal text-[13px] leading-[22px] text-[#111111]">
              Phone:{" "}
              <span className="font-semibold">{columnData.phone || "-"}</span>
            </p>
            <p className="font-normal text-[13px] leading-[22px] text-[#111111]">
              Role: <span className="font-semibold">{columnData.roleName}</span>
            </p>
          </div>
        )
      },
    },
  ]

  const columns: SimpleTableProps["columns"] = [
    {
      title: "Name",
      dataIndex: "fullname",
      sortable: true,
      render: (columnData: GroupMemberResponse) => (
        <div className="flex items-center gap-[8px] pl-[20px]">
          <Avatar
            name={columnData.fullname ?? ""}
            avatar={columnData.avatar ?? ""}
            width="30px"
            height="30px"
          />
          <div className="flex flex-col">
            <p className="font-semibold text-[13px] leading-[22px] text-[#111111]">
              {columnData.fullname}
            </p>
            <p className="font-normal text-[13px] leading-[22px] text-[#7A7A7A]">
              {columnData.email}
            </p>
          </div>
        </div>
      ),
    },

    {
      title: "Phone",
      dataIndex: "phone",
      sortable: true,
      width: "15%",
      render: (columnData: GroupMemberResponse) => columnData.phone || "-",
    },

    {
      title: "Role",
      dataIndex: "role",
      sortable: true,
      width: "15%",
      render: (columnData: GroupMemberResponse) => {
        return (
          <p
            className="font-semibold text-[13px] leading-[22px] text-[#111111]"
            style={{
              whiteSpace: "nowrap",
            }}
          >
            {columnData.roleName}
          </p>
        )
      },
    },
    {
      title: "",
      dataIndex: "action",
      align: "right",
      width: "30px",
      render: (columnData: GroupMemberResponse) => {
        const currentUser = userInfo.id === columnData.id
        if (currentUser) {
          return null
        }
        const isAction = canRemoveMember || canChangeRole
        if (!isAction) {
          return null
        }
        return (
          <TippyCustomzie
            containerClass="component-card-menu"
            placement="bottom-end"
            interactive
            arrow={false}
            animation="scale"
            visible={showTippy.includes(columnData.id)}
            onClickOutside={() =>
              setShowTippy(showTippy.filter((item) => item !== columnData.id))
            }
            content={
              <div
                style={{
                  background: "#222222",
                  borderRadius: 4,
                  paddingTop: 8,
                  paddingBottom: 8,
                  width: 120,
                }}
              >
                {canRemoveMember ? (
                  <ActionItem
                    title="Remove"
                    onClick={() => {
                      handleClickTippy(columnData.id)
                      handleRemoveMember(columnData.id)
                    }}
                  />
                ) : null}
                {canChangeRole ? (
                  <ActionItem
                    title="Change role"
                    onClick={() => {
                      handleClickTippy(columnData.id)
                      openChangeRoleModal(columnData)
                    }}
                  />
                ) : null}
              </div>
            }
            allowHTML
          >
            <div
              className="flex items-center justify-end cursor-pointer"
              onClick={() => {
                handleClickTippy(columnData.id)
              }}
            >
              <IconActionThreeDot />
            </div>
          </TippyCustomzie>
        )
      },
    },
  ]

  return (
    <div className="h-full flex flex-col justify-between overflow-hidden">
      <div className="bg-[#FFFFFF] border-b border-[#E4E4E4] h-[63px]">
        {renderHeader()}
      </div>
      <div className="flex-1 overflow-auto">{renderContent()}</div>
      {changeRoleModal.open ? (
        <ChangeGroupRoleModal
          isOpen={changeRoleModal.open}
          onClose={handleCloseChangeRoleModal}
          onSuccess={handleChangeRoleSuccess}
          groupId={groupId}
          memberId={changeRoleModal.memberId}
          currentRoleId={changeRoleModal.currentRoleId}
          roles={roles}
        />
      ) : null}
      {openModalAddMember.value ? (
        <AddMemberToGroup
          isOpen={openModalAddMember.value}
          onClose={() => openModalAddMember.setValue(false)}
          groupId={groupId}
          callback={handleWhenAddMemberSuccess}
          roles={roles}
        />
      ) : null}
    </div>
  )
}
